import React, { useRef, useState } from 'react';
import { achievements } from '../utils/achievements';
import { useTranslation } from '../i18n';
import { bookmarkLink, getAchievementLevel } from '../utils/functions';
import closeIcon from '../images/close-line.svg';
import moment from 'moment';
import 'moment/locale/sv';

const AchievementsCard = ({ userData, theme }) => {
  const lang = localStorage.getItem('language');
  const { t } = useTranslation();
  const [dialogIsOpen, setDialogOpen] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState(null);

  const dialogRef = useRef(null);

  const userProgress = userData.achievements;
  const unlockedAchievements = achievements.filter((achievement) =>
    userProgress.achievementsUnlocked?.includes(achievement.id),
  );
  const lockedAchievements = achievements.filter(
    (achievement) =>
      !userProgress.achievementsUnlocked?.includes(achievement.id),
  );

  const openDialog = (achievementId) => {
    if (dialogRef.current) {
      setDialogOpen(true);
      dialogRef.current.showModal();
    }
    const achievement = achievements?.find((ach) => ach.id === achievementId);
    const unlockInfo = userProgress?.achievementsHistory?.find(
      (a) => a.id === achievementId,
    );
    setSelectedAchievement({ ...achievement, ...unlockInfo });
  };

  const closeDialog = () => {
    if (dialogRef.current) {
      setDialogOpen(false);
      dialogRef.current.close();
    }
    setSelectedAchievement(null);
  };

  const AchievementSection = ({ achievements, title, status }) =>
    achievements.length > 0 && [
      <h3 key={title} className="fs-m pt-65">
        {title}
      </h3>,
      <div key={status} className="achievements-grid">
        {achievements.map((achievement) => (
          <div
            key={achievement.id}
            className={`achievement-item ${status} ${getAchievementLevel(achievement.level)}`}
            onClick={() => openDialog(achievement.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                openDialog(achievement.id);
              }
            }}
            tabIndex={0}
            aria-label={t.trophies[achievement.locale].name}
          >
            <img
              src={bookmarkLink(achievement.level, theme !== 'light')}
              alt={achievement.name}
              height={64}
              width={64}
              className="achievement-icon"
            />
            <div className="achievement-number">
              {achievement.displayNumber.toLocaleString()}
            </div>
            <div className="achievement-label">
              {t.trophies[achievement.locale].name}
            </div>
          </div>
        ))}
      </div>,
    ];

  return [
    <div key="achievements" className="card-wrapper">
      <div className="card">
        <AchievementSection
          achievements={unlockedAchievements}
          title={t.unlocked}
          status="unlocked"
        />

        {unlockedAchievements.length > 0 && lockedAchievements.length > 0 && (
          <div className="separator"></div>
        )}

        <AchievementSection
          achievements={lockedAchievements}
          title={t.locked}
          status="locked"
        />
      </div>
    </div>,
    <dialog ref={dialogRef} key="dialog">
      {dialogIsOpen && (
        <div className="fullscreen-container" tabIndex={-1}>
          <button className={`fullscreen-close`} onClick={closeDialog}>
            <img src={closeIcon} width={42} height={42} alt={t.close} />
            {t.close}
          </button>

          <div className="card-wrapper">
            <div className="card">
              <div
                className={`achievement-card unlocked ${getAchievementLevel(selectedAchievement.level)}`}
              >
                <img
                  src={bookmarkLink(
                    selectedAchievement.level,
                    theme !== 'light',
                  )}
                  alt={selectedAchievement.name}
                  height={128}
                  width={128}
                />
                <div className="achievement-number standalone">
                  {selectedAchievement.displayNumber.toLocaleString()}
                </div>
                <div className="fs-l">
                  {t.trophies[selectedAchievement.locale].name}
                </div>
                <div className="achievement-info">
                  <div className="fs-m pt-65">
                    {t.trophies[selectedAchievement.locale].desc}
                  </div>
                  {selectedAchievement.dateUnlocked && (
                    <div className="fs-m pt-65">
                      {t.achievementunlocked}
                      {`: `}
                      {moment(selectedAchievement.dateUnlocked)
                        .locale(lang)
                        .format('D MMM, YYYY')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="btn-group-wrapper">
            <div className="btn-group">
              <button
                className="btn btn-primary w-100"
                onClick={() => closeDialog()}
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </dialog>,
  ];
};

export default AchievementsCard;
